/**
 * 💎 Subscription Service
 *
 * Application layer لإدارة الاشتراكات:
 *   - getProviderSubscription: قراءة حالة provider
 *   - subscribeToProviderSubscription: real-time updates
 *   - extendTrial / markAsPaid / suspendProvider (admin only — via Cloud Functions)
 *
 * 🛡️ Security: كل operations الحساسة تتم via Cloud Functions
 *    (admin check server-side، Firestore transaction، audit log).
 */

import {
  doc,
  getDoc,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';

import { db } from '@infrastructure/firebase/config';
import {
  callExtendTrial,
  callMarkAsPaid,
  callSuspendProvider,
} from '@infrastructure/firebase/functions';
import type {
  SubscriptionStatus,
  SubscriptionPlan,
} from '@domain/entities/Provider';

// ═══════════════════════════════════════════════════
// Types (View Models)
// ═══════════════════════════════════════════════════

export interface ProviderSubscription {
  providerId: string;
  status: SubscriptionStatus;
  trialStartedAt: Date | null;
  trialEndsAt: Date | null;
  trialDurationDays: number;
  daysRemaining: number; // -1 لو ما في trial

  // Paid
  plan: SubscriptionPlan | null;
  subscriptionEndsAt: Date | null;
  lastPaymentAt: Date | null;
  lastPaymentAmount: number | null;

  // Helpers
  isActive: boolean;
  isExpired: boolean;
  isInTrial: boolean;
  isEndingSoon: boolean;
}

// ═══════════════════════════════════════════════════
// Service
// ═══════════════════════════════════════════════════

class SubscriptionService {
  /**
   * 📖 جلب حالة الاشتراك لـ provider معيّن
   */
  async getProviderSubscription(
    providerId: string
  ): Promise<ProviderSubscription | null> {
    const snap = await getDoc(doc(db, 'providers', providerId));
    if (!snap.exists()) return null;
    return this.mapToViewModel(providerId, snap.data());
  }

  /**
   * 🔴 Real-time subscription لتحديث الـ status فوراً
   */
  subscribeToProviderSubscription(
    providerId: string,
    callback: (sub: ProviderSubscription | null) => void,
    onError?: (err: Error) => void
  ): Unsubscribe {
    return onSnapshot(
      doc(db, 'providers', providerId),
      (snap) => {
        if (!snap.exists()) {
          callback(null);
          return;
        }
        callback(this.mapToViewModel(providerId, snap.data()));
      },
      (err) => onError?.(err)
    );
  }

  // ═══════════════════════════════════════════════════
  // Admin Actions (via Cloud Functions)
  // ═══════════════════════════════════════════════════

  /**
   * 🔄 تمديد الفترة التجريبية
   */
  async extendTrial(
    providerId: string,
    daysToAdd: number,
    reason: string
  ): Promise<void> {
    await this.callOrTranslate(() =>
      callExtendTrial({ providerId, daysToAdd, reason })
    );
  }

  /**
   * 💳 تحويل لـ paid_active
   */
  async markAsPaid(
    providerId: string,
    plan: SubscriptionPlan,
    durationDays: number,
    paymentAmount: number,
    paymentNote: string
  ): Promise<void> {
    await this.callOrTranslate(() =>
      callMarkAsPaid({
        providerId,
        plan,
        durationDays,
        paymentAmount,
        paymentNote,
      })
    );
  }

  /**
   * 🚫 إيقاف provider
   */
  async suspend(providerId: string, reason: string): Promise<void> {
    await this.callOrTranslate(() =>
      callSuspendProvider({ providerId, reason })
    );
  }

  // ═══════════════════════════════════════════════════
  // Helpers
  // ═══════════════════════════════════════════════════

  private async callOrTranslate<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      const code = error.code || '';
      const message = error.message || 'فشلت العملية';

      if (code.includes('permission-denied')) {
        throw new Error('لا تملك صلاحية لهذه العملية');
      }
      if (code.includes('not-found')) {
        throw new Error('المزوّد غير موجود');
      }
      if (code.includes('invalid-argument') || code.includes('failed-precondition')) {
        throw new Error(message);
      }

      throw new Error(message);
    }
  }

  private mapToViewModel(
    providerId: string,
    data: Record<string, unknown>
  ): ProviderSubscription {
    const status = (data['subscriptionStatus'] as SubscriptionStatus) ?? 'paid_active';
    const trialStartedAt = this.parseDate(data['trialStartedAt']);
    const trialEndsAt = this.parseDate(data['trialEndsAt']);

    const now = new Date();
    const daysRemaining =
      trialEndsAt && (status === 'trial_active' || status === 'trial_ending_soon')
        ? Math.max(
            0,
            Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          )
        : -1;

    const isActive =
      status === 'trial_active' ||
      status === 'trial_ending_soon' ||
      status === 'paid_active';
    const isExpired =
      status === 'trial_expired' ||
      status === 'paid_expired' ||
      status === 'suspended';
    const isInTrial =
      status === 'trial_active' || status === 'trial_ending_soon';
    const isEndingSoon = status === 'trial_ending_soon';

    return {
      providerId,
      status,
      trialStartedAt,
      trialEndsAt,
      trialDurationDays: (data['trialDurationDays'] as number) ?? 30,
      daysRemaining,
      plan: (data['subscriptionPlan'] as SubscriptionPlan) ?? null,
      subscriptionEndsAt: this.parseDate(data['subscriptionEndsAt']),
      lastPaymentAt: this.parseDate(data['lastPaymentAt']),
      lastPaymentAmount: (data['lastPaymentAmount'] as number) ?? null,
      isActive,
      isExpired,
      isInTrial,
      isEndingSoon,
    };
  }

  private parseDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'object' && value !== null && 'toDate' in value) {
      try {
        return (value as { toDate: () => Date }).toDate();
      } catch {
        return null;
      }
    }
    if (typeof value === 'string') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  }
}

export const subscriptionService = new SubscriptionService();
